<script>
import { getProductDetail, updateProduct, deleteProduct, toggleProductView, uploadProductImage, deleteProductImage, getProductAccountList } from '@/api/product.js'
import { getCategoryList } from '@/api/category.js'
import { getBrandList } from '@/api/brand.js'
import { getSellerList } from '@/api/product.js'
import { getProductLabels } from '@/api/productLabel.js'
import { getProductDeliveryExchangeTemplates } from '@/api/productDeliveryExchangeTemplate.js'
import { getAdminInfo } from '@/utils/token.js'
import { IMAGE_SPECS, UPLOAD_ACCEPT, summaryText } from '@/constants/imageSpecs.js'

export default {
  name: 'ProductDetailPage',

  data() {
    return {
      UPLOAD_ACCEPT,
      LIST_IMAGE_NOTE: IMAGE_SPECS.PRODUCT_LIST.safeArea + ' ' + IMAGE_SPECS.PRODUCT_LIST.note,
      DETAIL_IMAGE_NOTE: IMAGE_SPECS.PRODUCT_DETAIL.note,
      product: null,
      editMode: false,
      editForm: {},
      categoryOptions: [{ title: '카테고리 선택', value: '' }],
      brandOptions: [{ title: '브랜드 선택', value: '' }],
      sellerOptions: [{ title: '공급사 선택', value: '' }],
      deliveryExchangeTemplateOptions: [{ title: '배송/교환 안내 미선택', value: '' }],
      labelOptions: [],
      productStatusOptions: [
        { title: '판매중', value: 'ON_SALE' },
        { title: '숨김', value: 'HIDDEN' },
        { title: '판매종료', value: 'ENDED' },
      ],
      stockStatusOptions: [
        { title: '재고있음', value: 'IN_STOCK' },
        { title: '품절', value: 'OUT_OF_STOCK' },
        { title: '입고예정', value: 'COMING_SOON' },
        { title: '판매중지', value: 'STOPPED' },
      ],
      priceDisplayTypeOptions: [
        { title: '시세 연동', value: 'MARKET' },
        { title: '고정가', value: 'FIXED' },
        { title: '가격문의', value: 'CONTACT' },
      ],
      discountTypeOptions: [
        { title: '없음', value: 'NONE' },
        { title: '상품가 할인', value: 'PRODUCT_PRICE' },
        { title: '마진 할인', value: 'MARGIN' },
      ],
      paymentTypeOptions: [
        { title: '계좌이체', value: 'CASH' },
        { title: '카드결제', value: 'CARD' },
      ],
      cashAccountOptions: [{ title: '입금계좌 선택', value: '' }],
      loading: false,
      saving: false,
      deleting: false,
      toggling: false,
      uploading: false,
      deleteDialog: false,
      errorMsg: '',
      saveErrorMsg: '',
      adminInfo: {},
    }
  },

  computed: {
    listImageSpec() { return summaryText('PRODUCT_LIST') },
    detailImageSpec() { return summaryText('PRODUCT_DETAIL') },
    productCode() {
      return this.$route.params.productCode
    },
    isHq() {
      return this.adminInfo?.adminType === 'HQ'
    },
  },

  mounted() {
    this.adminInfo = getAdminInfo()
    Promise.all([this.fetchDetail(), this.fetchCategories(), this.fetchBrands(), this.fetchSellers(), this.fetchLabels(), this.fetchDeliveryExchangeTemplates(), this.fetchCashAccounts()])
  },

  methods: {
    async fetchDetail() {
      this.loading = true
      this.errorMsg = ''
      try {
        const res = await getProductDetail(this.productCode)
        if (res.data?.code === 0) {
          this.product = res.data
        } else {
          this.errorMsg = res.data?.message || '상품 정보를 불러오지 못했습니다.'
        }
      } catch (error) {
        console.error('[ProductDetailPage] fetchDetail error:', error)
        this.errorMsg = '상품 정보를 불러오는 중 오류가 발생했습니다.'
      } finally {
        this.loading = false
      }
    },

    async fetchCategories() {
      try {
        const res = await getCategoryList({ page: 1, size: 100 })
        if (res.data?.code === 0) {
          const list = [...(res.data.data?.items || [])].sort(this.sortByOrder)
          this.categoryOptions = [
            { title: '카테고리 선택', value: '' },
            ...list.map(item => ({ title: item.categoryName, value: item.categoryCode })),
          ]
        }
      } catch (error) {
        console.error('[ProductDetailPage] fetchCategories error:', error)
      }
    },

    async fetchSellers() {
      try {
        const res = await getSellerList()
        if (res.data?.code === 0) {
          this.sellerOptions = [
            { title: '공급사 선택', value: '' },
            ...(res.data.items || []).map(item => ({ title: item.sellerName, value: item.sellerCode })),
          ]
        }
      } catch (error) {
        console.error('fetchSellers error:', error)
      }
    },

    async fetchBrands() {
      try {
        const res = await getBrandList({ page: 1, size: 100, activeYn: 'Y' })
        if (res.data?.code === 0) {
          this.brandOptions = [
            { title: '브랜드 선택', value: '' },
            ...(res.data.data?.items || []).map(item => ({ title: item.brandName, value: item.brandCode })),
          ]
        }
      } catch (error) {
        console.error('[ProductDetailPage] fetchBrands error:', error)
      }
    },

    async fetchLabels() {
      try {
        const res = await getProductLabels({ activeYn: 'Y' })
        if (res.data?.code === 0) {
          this.labelOptions = (res.data.items || []).map(item => ({
            title: item.labelName,
            value: item.productLabelCode,
          }))
        }
      } catch (error) {
        console.error('[ProductDetailPage] fetchLabels error:', error)
      }
    },

    async fetchDeliveryExchangeTemplates() {
      try {
        const res = await getProductDeliveryExchangeTemplates({ activeYn: 'Y', page: 1, size: 200 })
        if (res.data?.code === 0) {
          const items = res.data.data?.items || res.data.items || []
          this.deliveryExchangeTemplateOptions = [
            { title: '배송/교환 안내 미선택', value: '' },
            ...items.map(item => ({
              title: `${item.scopeType === 'BRANCH' ? `[${item.branchName || '지점'}]` : '[본사]'} ${item.templateName}`,
              value: item.productDeliveryExchangeTemplateCode,
            })),
          ]
        }
      } catch (error) {
        console.error('[ProductDetailPage] fetchDeliveryExchangeTemplates error:', error)
      }
    },

    async fetchCashAccounts() {
      try {
        const res = await getProductAccountList({ activeYn: 'Y', page: 1, size: 500 })
        if (res.data?.code === 0) {
          const items = res.data.data?.items || []
          this.cashAccountOptions = [
            { title: '입금계좌 선택', value: '' },
            ...items.map(item => ({
              title: `${item.branchName || '본사/지점'} · ${item.bankName} ${item.accountNo} · ${item.accountHolder} (${this.formatAccountRange(item)})`,
              value: item.branchAccountCode,
            })),
          ]
        }
      } catch (error) {
        console.error('[ProductDetailPage] fetchCashAccounts error:', error)
      }
    },

    sortByOrder(a, b) {
      const ao = Number.isFinite(Number(a?.orderby)) ? Number(a.orderby) : Number.MAX_SAFE_INTEGER
      const bo = Number.isFinite(Number(b?.orderby)) ? Number(b.orderby) : Number.MAX_SAFE_INTEGER
      if (ao !== bo) return ao - bo
      return String(b?.insertDt || '').localeCompare(String(a?.insertDt || ''))
    },

    enterEditMode() {
      this.editForm = {
        productName: this.product.productName || '',
        categoryCode: this.product.categoryCode || '',
        brandCode: this.product.brandCode || '',
        sellerCode: this.product.sellerCode || '',
        deliveryExchangeTemplateCode: this.product.deliveryExchangeTemplateCode || '',
        productText: this.product.productText || '',
        currency: this.product.currency || 'KRW',
        conventionCost: this.product.conventionCost ?? null,
        weight: this.product.weight ?? null,
        purity: this.product.purity ?? null,
        weightEditableYn: this.product.weightEditableYn || 'Y',
        orderby: this.product.orderby ?? null,
        skuCode: this.product.skuCode || '',
        stockQty: this.product.stockQty ?? 0,
        productStatus: this.product.productStatus || 'ON_SALE',
        stockStatus: this.product.stockStatus || 'IN_STOCK',
        priceDisplayType: this.product.priceDisplayType || 'MARKET',
        discountType: this.product.discountType || 'NONE',
        discountRate: this.product.discountRate ?? null,
        discountAmount: this.product.discountAmount ?? null,
        originalPrice: this.product.originalPrice ?? null,
        discountLabel: this.product.discountLabel || '',
        manufacturer: this.product.manufacturer || '',
        originCountry: this.product.originCountry || '',
        searchKeywords: this.product.searchKeywords || '',
        labelCodes: (this.product.labels || []).map(item => item.productLabelCode),
        paymentTypes: this.product.paymentTypes?.length ? [...this.product.paymentTypes] : ['CASH', 'CARD'],
        cashBranchAccountCode: this.product.cashBranchAccountCode || '',
      }
      this.saveErrorMsg = ''
      this.editMode = true
    },

    cancelEdit() {
      this.editMode = false
      this.saveErrorMsg = ''
    },

    validate() {
      if (!this.editForm.productName?.trim()) return '상품명을 입력해주세요.'
      if (!this.editForm.categoryCode) return '카테고리를 선택해주세요.'
      if (!this.editForm.brandCode) return '브랜드를 선택해주세요.'
      if (!this.editForm.sellerCode) return '공급사를 선택해주세요.'
      if (!this.editForm.weight || Number(this.editForm.weight) <= 0) return '중량을 입력해주세요.'
      if (!this.editForm.purity || Number(this.editForm.purity) <= 0) return '순도를 입력해주세요.'
      if (Number(this.editForm.purity) > 1000) return '순도는 1000 이하로 입력해주세요.'
      if (this.editForm.orderby !== null && this.editForm.orderby !== '' && Number(this.editForm.orderby) < 0) return '정렬순서는 0 이상이어야 합니다.'
      if (!this.editForm.paymentTypes?.length) return '결제방법을 하나 이상 선택해 주세요.'
      if (this.editForm.paymentTypes.includes('CASH') && !this.editForm.cashBranchAccountCode) return '계좌이체 결제에 사용할 입금계좌를 선택해 주세요.'
      return ''
    },

    buildPayload() {
      return {
        productName: this.editForm.productName.trim(),
        categoryCode: this.editForm.categoryCode,
        brandCode: this.editForm.brandCode,
        sellerCode: this.editForm.sellerCode,
        deliveryExchangeTemplateCode: this.editForm.deliveryExchangeTemplateCode || null,
        productText: this.editForm.productText || null,
        currency: this.editForm.currency || null,
        conventionCost: this.toNumberOrNull(this.editForm.conventionCost),
        weight: this.toNumberOrNull(this.editForm.weight),
        purity: this.toNumberOrNull(this.editForm.purity),
        weightEditableYn: this.editForm.weightEditableYn || 'Y',
        orderby: this.toNumberOrNull(this.editForm.orderby),
        skuCode: this.editForm.skuCode || null,
        stockQty: this.toNumberOrNull(this.editForm.stockQty) ?? 0,
        productStatus: this.editForm.productStatus,
        stockStatus: this.editForm.stockStatus,
        priceDisplayType: this.editForm.priceDisplayType,
        discountType: this.editForm.discountType,
        discountRate: this.toNumberOrNull(this.editForm.discountRate),
        discountAmount: this.toNumberOrNull(this.editForm.discountAmount),
        originalPrice: this.toNumberOrNull(this.editForm.originalPrice),
        discountLabel: this.editForm.discountLabel || null,
        manufacturer: this.editForm.manufacturer || null,
        originCountry: this.editForm.originCountry || null,
        searchKeywords: this.editForm.searchKeywords || null,
        labelCodes: this.editForm.labelCodes,
        paymentTypes: this.editForm.paymentTypes,
        cashBranchAccountCode: this.editForm.paymentTypes.includes('CASH') ? this.editForm.cashBranchAccountCode : null,
      }
    },

    formatAccountRange(item) {
      const min = Number(item.minAmount || 0).toLocaleString('ko-KR')
      const max = item.maxAmount === null || item.maxAmount === undefined ? '제한 없음' : Number(item.maxAmount).toLocaleString('ko-KR')
      return `${min} ~ ${max}`
    },

    toNumberOrNull(value) {
      if (value === '' || value === null || value === undefined) return null
      const number = Number(value)
      return Number.isNaN(number) ? null : number
    },

    async handleSave() {
      this.saveErrorMsg = this.validate()
      if (this.saveErrorMsg) return

      this.saving = true
      try {
        const res = await updateProduct(this.productCode, this.buildPayload())
        if (res.data?.code === 0) {
          this.product = res.data
          this.editMode = false
        } else {
          this.saveErrorMsg = res.data?.message || '수정 중 오류가 발생했습니다.'
        }
      } catch (error) {
        console.error('[ProductDetailPage] handleSave error:', error)
        this.saveErrorMsg = error?.response?.data?.message || '수정 중 오류가 발생했습니다.'
      } finally {
        this.saving = false
      }
    },

    async handleDelete() {
      this.deleting = true
      try {
        const res = await deleteProduct(this.productCode)
        if (res.data?.code === 0) {
          this.$router.push('/products')
        } else {
          this.errorMsg = res.data?.message || '삭제 중 오류가 발생했습니다.'
        }
      } catch (error) {
        console.error('[ProductDetailPage] handleDelete error:', error)
        this.errorMsg = '삭제 중 오류가 발생했습니다.'
      } finally {
        this.deleting = false
        this.deleteDialog = false
      }
    },

    async handleToggleView() {
      if (this.toggling) return
      this.toggling = true
      try {
        const res = await toggleProductView(this.productCode)
        if (res.data?.code === 0) {
          this.product = {
            ...this.product,
            viewYn: res.data.viewYn,
            productStatus: res.data.viewYn === 'Y' ? 'ON_SALE' : 'HIDDEN',
          }
        }
      } catch (error) {
        console.error('[ProductDetailPage] handleToggleView error:', error)
      } finally {
        this.toggling = false
      }
    },

    async handleImageUpload(fileType, event) {
      const file = event.target.files?.[0]
      event.target.value = ''
      if (!file || this.uploading) return

      this.uploading = true
      try {
        const res = await uploadProductImage(this.productCode, fileType, file)
        if (res.data?.code === 0) this.product = res.data
      } catch (error) {
        console.error('[ProductDetailPage] handleImageUpload error:', error)
      } finally {
        this.uploading = false
      }
    },

    async handleImageDelete(seq) {
      try {
        const res = await deleteProductImage(this.productCode, seq)
        if (res.data?.code === 0) this.product = res.data
      } catch (error) {
        console.error('[ProductDetailPage] handleImageDelete error:', error)
      }
    },

    formatDateTime(value) {
      return value ? value.substring(0, 16).replace('T', ' ') : '-'
    },

    formatMoney(value) {
      if (value === null || value === undefined || value === '') return '-'
      const number = Number(value)
      return Number.isNaN(number) ? '-' : number.toLocaleString('ko-KR')
    },

    statusText(value) {
      return { ON_SALE: '판매중', HIDDEN: '숨김', ENDED: '판매종료' }[value] || '-'
    },

    stockText(value) {
      return { IN_STOCK: '재고있음', OUT_OF_STOCK: '품절', COMING_SOON: '입고예정', STOPPED: '판매중지' }[value] || '-'
    },

    priceText(value) {
      return { MARKET: '시세 연동', MARKET_LINKED: '시세 연동', FIXED: '고정가', CONTACT: '가격문의', INQUIRY: '가격문의' }[value] || '-'
    },

    paymentTypeText(value) {
      return { CASH: '계좌이체', CARD: '카드결제' }[value] || value || '-'
    },

    cashAccountLabel(branchAccountCode) {
      if (!branchAccountCode) return '-'
      const found = this.cashAccountOptions.find(item => item.value === branchAccountCode)
      return found?.title || branchAccountCode
    },

    discountText(product) {
      if (product.discountLabel) return product.discountLabel
      if (product.discountType === 'PRODUCT_PRICE' && product.discountRate) return `${product.discountRate}%`
      if (product.discountType === 'MARGIN' && product.discountAmount) return `${this.formatMoney(product.discountAmount)}원`
      return '-'
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-space-between mb-5">
      <div>
        <div class="text-caption text-primary mb-1 breadcrumb-link" @click="$router.push('/products')">상품관리 / 상품 상세</div>
        <div class="text-h5 font-weight-bold">상품 상세</div>
        <div class="text-body-2 text-grey mt-1">상품 정보 조회 및 수정</div>
      </div>
      <div v-if="product" class="d-flex ga-2">
        <template v-if="!editMode">
          <v-btn variant="tonal" color="primary" prepend-icon="mdi-pencil-outline" @click="enterEditMode">수정</v-btn>
          <v-btn variant="tonal" color="error" prepend-icon="mdi-delete-outline" @click="deleteDialog = true">삭제</v-btn>
        </template>
        <template v-else>
          <v-btn variant="text" :disabled="saving" @click="cancelEdit">취소</v-btn>
          <v-btn color="primary" :loading="saving" prepend-icon="mdi-check" @click="handleSave">저장</v-btn>
        </template>
      </div>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />
    <v-alert v-if="errorMsg" type="error" variant="tonal" density="compact" class="mb-4">{{ errorMsg }}</v-alert>
    <v-alert v-if="saveErrorMsg" type="error" variant="tonal" density="compact" class="mb-4">{{ saveErrorMsg }}</v-alert>

    <template v-if="product">
      <v-card rounded="lg" elevation="1" class="mb-5">
        <v-card-title class="text-subtitle-1 font-weight-bold py-4">기본 정보</v-card-title>
        <v-divider />
        <v-card-text>
          <template v-if="!editMode">
            <div class="info-grid">
              <div><span>상품코드</span><strong>{{ product.productCode }}</strong></div>
              <div><span>SKU/판매코드</span><strong>{{ product.skuCode || '-' }}</strong></div>
              <div><span>상품명</span><strong>{{ product.productName || '-' }}</strong></div>
              <div><span>카테고리</span><strong>{{ product.categoryName || '-' }}</strong></div>
              <div><span>브랜드</span><strong>{{ product.brandName || '-' }}</strong></div>
              <div><span>공급사</span><strong>{{ product.sellerName || '미지정' }}</strong></div>
              <div><span>배송/교환 안내</span><strong>{{ product.deliveryExchangeTemplateName || '미선택' }}</strong></div>
              <div><span>중량</span><strong>{{ product.weight || '-' }}</strong></div>
              <div><span>순도</span><strong>{{ product.purity || '-' }}</strong></div>
              <div><span>임가공비</span><strong>{{ formatMoney(product.conventionCost) }}</strong></div>
              <div><span>제조사/민트</span><strong>{{ product.manufacturer || '-' }}</strong></div>
              <div><span>원산지</span><strong>{{ product.originCountry || '-' }}</strong></div>
              <div><span>정렬순서</span><strong>{{ product.orderby ?? '-' }}</strong></div>
              <div><span>등록일</span><strong>{{ formatDateTime(product.insertDt) }}</strong></div>
            </div>
            <div class="mt-5">
              <div class="text-caption text-medium-emphasis mb-2">상품 설명</div>
              <div class="text-body-2 description">{{ product.productText || '등록된 설명이 없습니다.' }}</div>
            </div>
          </template>

          <template v-else>
            <v-row>
              <v-col cols="12" md="6"><v-text-field v-model="editForm.productName" label="상품명 *" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-select v-model="editForm.categoryCode" :items="categoryOptions" label="카테고리 *" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-select v-model="editForm.brandCode" :items="brandOptions" label="브랜드 *" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-select v-model="editForm.sellerCode" :items="sellerOptions" label="공급사 *" variant="outlined" hint="판매자는 아우라, 상품을 대는 법인" persistent-hint /></v-col>
              <v-col cols="12" md="6"><v-select v-model="editForm.deliveryExchangeTemplateCode" :items="deliveryExchangeTemplateOptions" label="배송/교환 안내" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model="editForm.skuCode" label="SKU/판매코드" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model="editForm.weight" type="number" step="0.001" label="중량 *" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model="editForm.purity" type="number" step="0.01" label="순도 *" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model.number="editForm.conventionCost" type="number" label="임가공비" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model="editForm.manufacturer" label="제조사/민트" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model="editForm.originCountry" label="원산지" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model.number="editForm.orderby" type="number" min="0" label="정렬순서" variant="outlined" /></v-col>
              <v-col cols="12" md="6"><v-text-field v-model="editForm.searchKeywords" label="검색 키워드" variant="outlined" placeholder="쉼표로 구분" /></v-col>
              <v-col cols="12"><v-textarea v-model="editForm.productText" label="상품 설명" variant="outlined" rows="4" auto-grow /></v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>

      <v-card rounded="lg" elevation="1" class="mb-5">
        <v-card-title class="text-subtitle-1 font-weight-bold py-4">판매 설정</v-card-title>
        <v-divider />
        <v-card-text>
          <template v-if="!editMode">
            <div class="info-grid">
              <div><span>상품 상태</span><strong>{{ statusText(product.productStatus) }}</strong></div>
              <div><span>재고 상태</span><strong>{{ stockText(product.stockStatus) }}</strong></div>
              <div><span>재고 수량</span><strong>{{ product.stockQty ?? 0 }}</strong></div>
              <div><span>가격 표시</span><strong>{{ priceText(product.priceDisplayType) }}</strong></div>
              <div><span>기존가</span><strong>{{ formatMoney(product.originalPrice) }}</strong></div>
              <div><span>할인</span><strong>{{ discountText(product) }}</strong></div>
              <div><span>중량 수정</span><strong>{{ product.weightEditableYn === 'Y' ? '허용' : '불가' }}</strong></div>
              <div class="wide">
                <span>결제방법</span>
                <div class="d-flex flex-wrap ga-1">
                  <v-chip v-for="paymentType in product.paymentTypes || []" :key="paymentType" size="small" color="primary" variant="tonal">
                    {{ paymentTypeText(paymentType) }}
                  </v-chip>
                  <strong v-if="!(product.paymentTypes || []).length">-</strong>
                </div>
              </div>
              <div v-if="(product.paymentTypes || []).includes('CASH')" class="wide">
                <span>계좌이체 입금계좌</span>
                <strong>{{ cashAccountLabel(product.cashBranchAccountCode) }}</strong>
              </div>
              <div class="wide">
                <span>상품 라벨</span>
                <div class="d-flex flex-wrap ga-1">
                  <v-chip v-for="label in product.labels || []" :key="label.productLabelCode" size="small" color="primary" variant="tonal">
                    {{ label.labelName }}
                  </v-chip>
                  <strong v-if="!(product.labels || []).length">-</strong>
                </div>
              </div>
              <div>
                <span>노출여부</span>
                <v-switch
                  :model-value="product.viewYn === 'Y'"
                  color="primary"
                  density="compact"
                  hide-details
                  :disabled="toggling"
                  :loading="toggling"
                  @update:model-value="handleToggleView"
                />
              </div>
            </div>
          </template>

          <template v-else>
            <v-row>
              <v-col cols="12" md="3"><v-select v-model="editForm.productStatus" :items="productStatusOptions" label="상품 상태 *" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-select v-model="editForm.stockStatus" :items="stockStatusOptions" label="재고 상태 *" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model.number="editForm.stockQty" type="number" min="0" step="0.001" label="재고 수량 *" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-select v-model="editForm.priceDisplayType" :items="priceDisplayTypeOptions" label="가격 표시" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-select v-model="editForm.discountType" :items="discountTypeOptions" label="할인 방식" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model.number="editForm.originalPrice" type="number" label="기존가" variant="outlined" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model.number="editForm.discountRate" type="number" label="할인율(%)" variant="outlined" :disabled="editForm.discountType === 'NONE'" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model.number="editForm.discountAmount" type="number" label="할인금액" variant="outlined" :disabled="editForm.discountType === 'NONE'" /></v-col>
              <v-col cols="12" md="3"><v-text-field v-model="editForm.discountLabel" label="할인 문구" variant="outlined" /></v-col>
              <v-col cols="12" md="6"><v-select v-model="editForm.labelCodes" :items="labelOptions" label="상품 라벨" variant="outlined" chips multiple /></v-col>
              <v-col cols="12" md="6">
                <v-switch v-model="editForm.weightEditableYn" true-value="Y" false-value="N" color="primary" label="주문 시 중량 수기 수정 허용" hide-details />
              </v-col>
              <v-col cols="12">
                <div class="payment-box">
                  <div class="text-subtitle-2 font-weight-bold mb-2">결제방법 *</div>
                  <div class="d-flex flex-wrap ga-4">
                    <v-checkbox
                      v-for="option in paymentTypeOptions"
                      :key="option.value"
                      v-model="editForm.paymentTypes"
                      :label="option.title"
                      :value="option.value"
                      color="primary"
                      hide-details
                      density="compact"
                    />
                  </div>
                  <div class="text-caption text-medium-emphasis mt-2">
                    계좌이체 선택 시 본사/지점의 입금계좌 관리에 상품금액을 포함하는 활성 계좌가 있어야 저장됩니다.
                  </div>
                  <v-select
                    v-if="editForm.paymentTypes.includes('CASH')"
                    v-model="editForm.cashBranchAccountCode"
                    :items="cashAccountOptions"
                    label="입금계좌 *"
                    variant="outlined"
                    density="compact"
                    hide-details
                    class="mt-3"
                  />
                </div>
              </v-col>
            </v-row>
          </template>
        </v-card-text>
      </v-card>

      <v-card rounded="lg" elevation="1">
        <v-card-title class="text-subtitle-1 font-weight-bold py-4">이미지</v-card-title>
        <v-divider />
        <v-card-text>
          <div class="image-section">
            <div class="font-weight-bold mb-2">목록 이미지</div>
            <div class="text-caption text-grey mb-1">{{ listImageSpec }}</div>
            <div class="text-caption text-grey mb-3">{{ LIST_IMAGE_NOTE }}</div>
            <div class="image-list">
              <div v-for="img in product.thumbnailImages || []" :key="img.seq" class="image-item">
                <v-img :src="img.fileUrl" width="120" height="120" cover rounded="lg" />
                <v-btn v-if="editMode" icon size="x-small" color="error" class="image-delete" @click="handleImageDelete(img.seq)"><v-icon>mdi-close</v-icon></v-btn>
              </div>
              <span v-if="!(product.thumbnailImages || []).length" class="text-medium-emphasis">등록된 이미지가 없습니다.</span>
              <template v-if="editMode">
                <input ref="thumbInput" type="file" :accept="UPLOAD_ACCEPT" hidden @change="handleImageUpload('thumbnail', $event)">
                <v-btn variant="tonal" prepend-icon="mdi-plus" :loading="uploading" @click="$refs.thumbInput?.click()">이미지 추가</v-btn>
              </template>
            </div>
          </div>

          <div class="image-section mt-6">
            <div class="font-weight-bold mb-2">상세 이미지</div>
            <div class="text-caption text-grey mb-1">{{ detailImageSpec }}</div>
            <div class="text-caption text-grey mb-3">{{ DETAIL_IMAGE_NOTE }}</div>
            <div class="image-list">
              <div v-for="img in product.detailImages || []" :key="img.seq" class="image-item">
                <v-img :src="img.fileUrl" width="120" height="120" cover rounded="lg" />
                <v-btn v-if="editMode" icon size="x-small" color="error" class="image-delete" @click="handleImageDelete(img.seq)"><v-icon>mdi-close</v-icon></v-btn>
              </div>
              <span v-if="!(product.detailImages || []).length" class="text-medium-emphasis">등록된 이미지가 없습니다.</span>
              <template v-if="editMode">
                <input ref="detailInput" type="file" :accept="UPLOAD_ACCEPT" hidden @change="handleImageUpload('detail', $event)">
                <v-btn variant="tonal" prepend-icon="mdi-plus" :loading="uploading" @click="$refs.detailInput?.click()">이미지 추가</v-btn>
              </template>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </template>

    <v-dialog v-model="deleteDialog" max-width="420" persistent>
      <v-card rounded="lg">
        <v-card-title class="font-weight-bold">상품 삭제 확인</v-card-title>
        <v-card-text>
          <strong class="text-error">{{ product?.productName }}</strong> 상품을 삭제하시겠습니까?
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="deleting" @click="deleteDialog = false">취소</v-btn>
          <v-btn color="error" :loading="deleting" @click="handleDelete">삭제</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.breadcrumb-link {
  cursor: pointer;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.info-grid > div {
  display: flex;
  min-height: 64px;
  flex-direction: column;
  justify-content: center;
  gap: 6px;
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
}

.info-grid span {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.58);
}

.info-grid .wide {
  grid-column: span 2;
}

.description {
  white-space: pre-wrap;
  line-height: 1.8;
}

.image-list {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.image-item {
  position: relative;
}

.image-delete {
  position: absolute;
  top: 4px;
  right: 4px;
}

.payment-box {
  padding: 14px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.03);
}

@media (max-width: 1100px) {
  .info-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
